import React, { useMemo } from 'react';

import { Alert, Box, Typography } from '@mui/material';

import TabPanelShell from '~/components/Offers/Detail/OfferBuilder/shared/TabPanelShell';

import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';

import {
  usePackageOptionsContext,
  usePricingToolSession,
  usePropertySetupContext,
  useSurchargeGridsContext,
} from '../../PricingToolSessionContext';
import SectionLabel from '../../shared/SectionLabel';
import { egsRooms, knownEgsRooms } from '../egs/egsModel';

import SurchargePeriodsSection from './SurchargePeriodsSection';

interface Props {
  offerId?: string;
}

export default function SurchargesTab({ offerId }: Props) {
  const { session, addPeriod, updatePeriod, removePeriod } = useSurchargeGridsContext();
  const { data: related } = useGetOpportunityRelated(offerId);
  const { roomAssets, optionState } = usePackageOptionsContext();
  const { draftIdBySfid } = usePricingToolSession();
  const { values: propertySetup, fx, currencyOptions, audRateFor } = usePropertySetupContext();
  const rooms = useMemo(() => egsRooms(optionState.roomsByCategory), [optionState.roomsByCategory]);
  const namedRooms = useMemo(
    () => knownEgsRooms(related, optionState.roomsByCategory, roomAssets, draftIdBySfid),
    [related, optionState.roomsByCategory, roomAssets, draftIdBySfid],
  );

  // fx.rate is already 1 for AUD.
  const fxRateAudToNative = fx.rate ?? null;

  return (
    <TabPanelShell
      subtitle="Add the dated surcharge periods for this deal. Submitting sends them to Salesforce as Surcharge Periods."
      title="Surcharges"
    >
      {!fx.isLoading && fxRateAudToNative == null && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Set the currency and FX rate on the Property Setup tab. Until then you can still type sell amounts, but the
          converted and margin figures stay blank.
        </Alert>
      )}

      <Box>
        <SectionLabel>Dated surcharge periods</SectionLabel>
        <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
          A period adds its Sell AUD on every date in its range, or only on the days you pick.
        </Typography>
        <SurchargePeriodsSection
          audRateFor={audRateFor}
          currencyOptions={currencyOptions}
          defaultCurrency={propertySetup?.currency ?? fx.currency}
          fxRateAudToNative={fxRateAudToNative}
          grids={session.grids}
          onAdd={addPeriod}
          onRemove={removePeriod}
          onUpdate={updatePeriod}
          roomNames={namedRooms}
          rooms={rooms}
        />
      </Box>
    </TabPanelShell>
  );
}
